import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getBlogPostBySlug } from '../utils/api';
import { Calendar, Tag, ArrowLeft, Share2, Activity } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import SaveToIdeasButton from '../components/SaveToIdeasButton';
import Seo from '../components/Seo';

const stripHtml = (s) => (s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const data = await getBlogPostBySlug(slug);
      setPost(data);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/blog', { replace: true });
      }
      console.error('Failed to load blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className={isLoggedIn ? "flex items-center justify-center py-12" : "min-h-screen bg-slate-50 flex items-center justify-center"}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const description = stripHtml(post.meta_description || post.excerpt).slice(0, 160);

  return (
    <div className={isLoggedIn ? "" : "min-h-screen bg-slate-50"}>
      <Seo
        title={`${post.meta_title || post.title} – MyCBC Blog`}
        description={description}
        path={`/blog/${post.slug}`}
        image={post.featured_image}
        type="article"
        article={{ publishedTime: post.published_at, modifiedTime: post.updated_at }}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'MedicalWebPage',
          headline: post.title,
          description,
          url: `https://mycbc.health/blog/${post.slug}`,
          mainEntityOfPage: `https://mycbc.health/blog/${post.slug}`,
          image: post.featured_image || 'https://mycbc.health/og-image.png',
          datePublished: post.published_at,
          dateModified: post.updated_at,
          inLanguage: 'en',
          about: [
            { '@type': 'MedicalCondition', name: 'Chronic Lymphocytic Leukemia', alternateName: 'CLL' },
            { '@type': 'MedicalCondition', name: 'Small Lymphocytic Lymphoma', alternateName: 'SLL' },
          ],
          author: { '@type': 'Organization', name: 'MyCBC', url: 'https://mycbc.health' },
          publisher: {
            '@type': 'Organization',
            name: 'MyCBC',
            url: 'https://mycbc.health',
            logo: { '@type': 'ImageObject', url: 'https://mycbc.health/android-chrome-512x512-v5.png' },
          },
        }}
      />
      {/* Header - only show for non-logged-in users */}
      {!isLoggedIn && (
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">MyCBC</span>
              </Link>
              <nav className="flex items-center gap-4">
                <Link to="/blog" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                  Blog
                </Link>
                <Link to="/register" className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                  Join Free
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={isLoggedIn ? "py-6" : "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Back link */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Link>

        <article>
          {/* Featured Image */}
          {post.featured_image && (
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-72 sm:h-96 lg:h-[28rem] object-cover rounded-2xl mb-8"
            />
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(post.published_at)}</span>
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-1">
                <Tag className="h-4 w-4" />
                <span>{post.tags.join(', ')}</span>
              </div>
            )}
            <button 
              onClick={handleShare}
              className="flex items-center gap-1 hover:text-primary transition-colors ml-auto"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            {post.title}
          </h1>

          {/* Content */}
          <div 
            className="blog-content prose prose-slate prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-slate-900
              prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pt-4 prose-h2:border-t prose-h2:border-slate-200
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-slate-700 prose-p:leading-relaxed prose-p:my-4
              prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-strong:text-slate-900 prose-strong:font-semibold
              prose-ul:my-4 prose-ul:space-y-2
              prose-ol:my-4 prose-ol:space-y-2
              prose-li:text-slate-700 prose-li:leading-relaxed
              prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
              prose-figure:my-8
              prose-figcaption:text-center prose-figcaption:text-sm prose-figcaption:text-slate-500 prose-figcaption:mt-2
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-slate-50 prose-blockquote:rounded-r-lg prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:my-6
              prose-code:text-primary prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-pre:bg-slate-900 prose-pre:text-slate-100"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags at bottom */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span 
                    key={tag}
                    className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Save to Ideas - for logged in users */}
          {isLoggedIn && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <SaveToIdeasButton
                title={post.title}
                category="Blog"
                sourcePage="/blog"
                sourceUrl={`/blog/${slug}`}
              />
            </div>
          )}
        </article>

        {/* CTA - only show for non-logged-in users */}
        {!isLoggedIn && (
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-6 text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Track Your CLL Journey
            </h3>
            <p className="text-slate-600 mb-4">
              MyCBC helps you track blood results, symptoms, and treatments in one place.
            </p>
            <div className="flex justify-center">
              <Link 
                to="/register"
                className="px-6 py-2 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        )}
      </main>

      {!isLoggedIn && <Footer />}
    </div>
  );
};

export default BlogPostPage;
